import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import CertificationCard from './components/CertificationCard';
import CodingChallengeCard from './components/CodingChallengeCard';
import LearningGoalCard from './components/LearningGoalCard';
import SkillCategorySection from './components/SkillCategorySection';
import SkillsOverview from './components/SkillsOverview';
import {
  Certification,
  CodingChallenge,
  LearningGoal,
  SkillCategory
} from './types';

const SkillsPage = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'certifications' | 'learning' | 'challenges'>(
    'all'
  );

  const skillCategories: SkillCategory[] = [
    {
      id: 'frontend',
      name: 'Frontend Development',
      icon: 'Layout',
      color: '#1F6FEB',
      skills: [
        {
          id: 'react',
          name: 'React',
          category: 'Frontend Framework',
          proficiency: 90,
          yearsOfExperience: 2,
          icon: 'Atom',
          color: '#61DAFB',
          description:
            'Expert in building modern, responsive web applications with React 18, hooks, and context API.',
          projects: 15
        },
        {
          id: 'typescript',
          name: 'TypeScript',
          category: 'Programming Language',
          proficiency: 85,
          yearsOfExperience: 2,
          icon: 'FileCode2',
          color: '#3178C6',
          description:
            'Strong typing skills with interfaces, generics, and advanced TypeScript patterns.',
          projects: 12
        },
        {
          id: 'tailwind',
          name: 'Tailwind CSS',
          category: 'CSS Framework',
          proficiency: 95,
          yearsOfExperience: 2,
          icon: 'Palette',
          color: '#06B6D4',
          description:
            'Mastery in utility-first CSS framework for rapid UI development and responsive design.',
          projects: 18
        },
        {
          id: 'html',
          name: 'HTML5',
          category: 'Markup Language',
          proficiency: 95,
          yearsOfExperience: 3,
          icon: 'Code',
          color: '#E34F26',
          description:
            'Semantic HTML5 with accessibility best practices and modern web standards.',
          projects: 20
        },
        {
          id: 'css',
          name: 'CSS3',
          category: 'Styling',
          proficiency: 90,
          yearsOfExperience: 3,
          icon: 'Paintbrush',
          color: '#1572B6',
          description:
            'Advanced CSS3 animations, flexbox, grid, and responsive design techniques.',
          projects: 20
        },
        {
          id: 'javascript',
          name: 'JavaScript',
          category: 'Programming Language',
          proficiency: 88,
          yearsOfExperience: 3,
          icon: 'Braces',
          color: '#F7DF1E',
          description:
            'ES6+ features, async programming, DOM manipulation, and modern JavaScript patterns.',
          projects: 25
        }]

    },
    {
      id: 'backend',
      name: 'Backend Development',
      icon: 'Server',
      color: '#238636',
      skills: [
        {
          id: 'nodejs',
          name: 'Node.js',
          category: 'Runtime Environment',
          proficiency: 80,
          yearsOfExperience: 1.5,
          icon: 'Hexagon',
          color: '#339933',
          description:
            'Building scalable server-side applications with Express.js and RESTful APIs.',
          projects: 8
        },
        {
          id: 'python',
          name: 'Python',
          category: 'Programming Language',
          proficiency: 75,
          yearsOfExperience: 2,
          icon: 'Code2',
          color: '#3776AB',
          description:
            'Data structures, algorithms, and backend development with Flask and Django basics.',
          projects: 10
        },
        {
          id: 'sql',
          name: 'SQL',
          category: 'Database',
          proficiency: 70,
          yearsOfExperience: 1,
          icon: 'Database',
          color: '#4479A1',
          description:
            'Database design, complex queries, joins, and optimization techniques.',
          projects: 6
        }]

    },
    {
      id: 'tools',
      name: 'Development Tools',
      icon: 'Wrench',
      color: '#FD7E14',
      skills: [
        {
          id: 'git',
          name: 'Git',
          category: 'Version Control',
          proficiency: 85,
          yearsOfExperience: 2,
          icon: 'GitBranch',
          color: '#F05032',
          description:
            'Version control, branching strategies, pull requests, and collaborative workflows.',
          projects: 30
        },
        {
          id: 'vscode',
          name: 'VS Code',
          category: 'IDE',
          proficiency: 90,
          yearsOfExperience: 3,
          icon: 'Code',
          color: '#007ACC',
          description:
            'Advanced editor features, extensions, debugging, and productivity shortcuts.',
          projects: 35
        },
        {
          id: 'figma',
          name: 'Figma',
          category: 'Design Tool',
          proficiency: 75,
          yearsOfExperience: 1.5,
          icon: 'Figma',
          color: '#F24E1E',
          description:
            'UI/UX design, prototyping, component libraries, and design system creation.',
          projects: 12
        }]

    },
    {
      id: 'sysadmin',
      name: 'System Administration',
      icon: 'Server',
      color: '#DC143C',
      skills: [
        {
          id: 'linux',
          name: 'Linux',
          category: 'Operating System',
          proficiency: 80,
          yearsOfExperience: 2,
          icon: 'Terminal',
          color: '#FCC624',
          description:
            'Linux system administration, shell scripting, server configuration, and troubleshooting.',
          projects: 8
        },
        {
          id: 'windows-server',
          name: 'Windows Server',
          category: 'Operating System',
          proficiency: 75,
          yearsOfExperience: 1.5,
          icon: 'HardDrive',
          color: '#0078D4',
          description:
            'Windows Server administration, Active Directory, server building, configuration, and maintenance.',
          projects: 6
        }]

    }];


  const certifications: Certification[] = [
    {
      id: 'cert1',
      name: 'React Developer Certification',
      issuer: 'Meta',
      date: 'December 2023',
      credentialId: 'META-REACT-2023-12345',
      icon: 'Award',
      image: "https://images.unsplash.com/photo-1652964803882-b582a77dd18d",
      alt: 'Modern laptop displaying React certification course materials on desk with coffee cup and notebook'
    },
    {
      id: 'cert2',
      name: 'JavaScript Algorithms and Data Structures',
      issuer: 'freeCodeCamp',
      date: 'October 2023',
      credentialId: 'FCC-JS-2023-67890',
      icon: 'Code2',
      image: "https://images.unsplash.com/photo-1719253479632-61b2fdc77a65",
      alt: 'Computer screen showing JavaScript code with algorithm visualizations and data structure diagrams'
    },
    {
      id: 'cert3',
      name: 'Responsive Web Design',
      issuer: 'freeCodeCamp',
      date: 'August 2023',
      credentialId: 'FCC-RWD-2023-11223',
      icon: 'Layout',
      image: "https://images.unsplash.com/photo-1684242282290-997cabc3ad06",
      alt: 'Multiple devices showing responsive website design across desktop, tablet, and mobile screens'
    }];


  const learningGoals: LearningGoal[] = [
    {
      id: 'goal1',
      technology: 'Next.js',
      progress: 65,
      targetDate: 'March 2025',
      icon: 'Zap',
      color: '#000000'
    },
    {
      id: 'goal2',
      technology: 'GraphQL',
      progress: 45,
      targetDate: 'April 2025',
      icon: 'Network',
      color: '#E10098'
    },
    {
      id: 'goal3',
      technology: 'Docker',
      progress: 30,
      targetDate: 'May 2025',
      icon: 'Container',
      color: '#2496ED'
    },
    {
      id: 'goal4',
      technology: 'AWS',
      progress: 25,
      targetDate: 'June 2025',
      icon: 'Cloud',
      color: '#FF9900'
    }];


  const codingChallenges: CodingChallenge[] = [
    {
      id: 'challenge1',
      title: 'Two Sum Problem',
      difficulty: 'Easy',
      language: 'JavaScript',
      description:
        'Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target. You may assume that each input would have exactly one solution, and you may not use the same element twice.',
      completed: true,
      icon: 'CheckCircle2'
    },
    {
      id: 'challenge2',
      title: 'Binary Tree Traversal',
      difficulty: 'Medium',
      language: 'Python',
      description:
        'Implement three different tree traversal methods (inorder, preorder, postorder) for a binary tree. Return the values in the correct order for each traversal type.',
      completed: true,
      icon: 'GitBranch'
    },
    {
      id: 'challenge3',
      title: 'Dynamic Programming - Coin Change',
      difficulty: 'Hard',
      language: 'JavaScript',
      description:
        'You are given an integer array coins representing coins of different denominations and an integer amount representing a total amount of money. Return the fewest number of coins that you need to make up that amount.',
      completed: false,
      icon: 'Coins'
    }];


  const totalSkills = skillCategories.reduce((acc, cat) => acc + cat.skills.length, 0);
  const totalProjects = skillCategories.reduce(
    (acc, cat) => acc + cat.skills.reduce((sum, skill) => sum + skill.projects, 0),
    0
  );
  const averageProficiency = Math.round(
    skillCategories.reduce(
      (acc, cat) =>
        acc + cat.skills.reduce((sum, skill) => sum + skill.proficiency, 0) / cat.skills.length,
      0
    ) / skillCategories.length
  );

  const tabs = [
    { id: 'all', label: 'All Skills', icon: 'Grid3x3' },
    { id: 'certifications', label: 'Certifications', icon: 'Award' },
    { id: 'learning', label: 'Learning Goals', icon: 'Target' },
    { id: 'challenges', label: 'Coding Challenges', icon: 'Code2' }];


  return (
    <>
      <Helmet>
        <title>Skills & Expertise - Muhammad Fotih Abdullaev | Developer Portfolio</title>
        <meta
          name="description"
          content="Explore my technical skills, certifications, and ongoing learning journey in web development, programming, and software engineering." />

      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
                <Icon name="Sparkles" size={20} color="var(--color-primary)" />
                <span className="text-sm font-medium text-primary">Skills Laboratory</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Technical Skills & Expertise
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                A comprehensive showcase of my technical competencies, certifications, and ongoing
                learning journey in software development and computer science.
              </p>
            </div>

            <SkillsOverview
              totalSkills={totalSkills}
              totalProjects={totalProjects}
              totalCertifications={certifications.length}
              averageProficiency={averageProficiency} />


            <div className="flex flex-wrap gap-3 mb-8 justify-center">
              {tabs.map((tab) =>
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'outline'}
                  size="default"
                  iconName={tab.icon}
                  iconPosition="left"
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}>

                  {tab.label}
                </Button>
              )}
            </div>

            {activeTab === 'all' &&
              <div>
                {skillCategories.map((category) =>
                  <SkillCategorySection key={category.id} category={category} />
                )}
              </div>
            }

            {activeTab === 'certifications' &&
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={24} color="var(--color-accent)" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">
                      Certifications & Achievements
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Professional certifications and course completions
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {certifications.map((cert) =>
                    <CertificationCard key={cert.id} certification={cert} />
                  )}
                </div>
              </div>
            }

            {activeTab === 'learning' &&
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Target" size={24} color="var(--color-secondary)" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Current Learning Goals</h2>
                    <p className="text-sm text-muted-foreground">
                      Technologies I'm actively learning and mastering
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {learningGoals.map((goal) =>
                    <LearningGoalCard key={goal.id} goal={goal} />
                  )}
                </div>
              </div>
            }

            {activeTab === 'challenges' &&
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Code2" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Coding Challenges</h2>
                    <p className="text-sm text-muted-foreground">
                      Algorithm and data structure problems I've solved
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {codingChallenges.map((challenge) =>
                    <CodingChallengeCard key={challenge.id} challenge={challenge} />
                  )}
                </div>
              </div>
            }

            <div className="mt-16 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-8 lg:p-12 border border-primary/20 text-center">
              <div className="w-16 h-16 bg-background/50 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Rocket" size={32} color="var(--color-primary)" />
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Ready to Build Something Amazing?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Let's collaborate on your next project. I bring fresh perspectives, solid
                fundamentals, and a passion for creating innovative solutions.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => window.location.href = '/collaboration'}>

                  Start Collaboration
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="FolderGit2"
                  iconPosition="left"
                  onClick={() => window.location.href = '/projects'}>

                  View Projects
                </Button>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-surface border-t border-border py-8 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Muhammad Fotih Abdullaev. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>);

};

export default SkillsPage;